import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { parseName } from '../../../utils/math-utils'
import { useApi } from '../../../hooks/useApi'
import CustomInput from '../../general/Form'

interface ApiSetting {
  _id: string
  rateLimitRequests: number
  rateLimitWindowMs: number
  corsAllowedOrigins: string
}

const ApiSettings: React.FC = () => {
  const { callApi } = useApi()
  const [apiSettings, setApiSettings] = useState<ApiSetting | null>(null)
  const [message, setMessage] = useState('')
  const [editedSettings, setEditedSettings] = useState<Partial<ApiSetting> | null>(null)

  useEffect(() => {
    callApi('/api/settings')
      .then((data) => { setApiSettings(data); setEditedSettings(data) })
      .catch((e) => console.error('Error fetching API settings:', e))
  }, [])

  const handleSaveClick = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const apiData = {
        ...editedSettings,
        rateLimitRequests: parseFloat(String(editedSettings?.rateLimitRequests)),
        rateLimitWindowMs: parseFloat(String(editedSettings?.rateLimitWindowMs)),
        corsAllowedOrigins: Array.isArray(editedSettings?.corsAllowedOrigins)
          ? editedSettings.corsAllowedOrigins
          : editedSettings?.corsAllowedOrigins?.split(','),
      }
      await callApi('/api/settings', apiData, 'PUT')
    } catch (e) {
      console.error('Error saving API settings:', e)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setEditedSettings((prev: any) => ({ ...prev, [name]: value }))
  }

  if (!apiSettings) return <Loading>Loading configurations…</Loading>

  return (
    <Form onSubmit={handleSaveClick}>
      {message !== '' ? (
        <FeedbackBanner>
          <span>{message}</span>
          <CloseBtn onClick={() => setMessage('')}>✕</CloseBtn>
        </FeedbackBanner>
      ) : (
        <>
          <FieldGrid>
            {Object.entries(editedSettings as ApiSetting).map(
              ([key, value]) =>
                !['__v', 'createdAt', 'updatedAt', 'lastUpdate', '_id'].includes(key) && (
                  <div key={key}>
                    <CustomInput
                      type="text"
                      name={key}
                      label={parseName(key)}
                      value={String(value)}
                      onChange={handleInputChange}
                      disabled={key === 'updatedAt'}
                    />
                  </div>
                )
            )}
          </FieldGrid>
          <Actions>
            <SaveBtn type="submit">Save</SaveBtn>
            <CancelBtn type="button" onClick={() => setEditedSettings(apiSettings)}>Cancel</CancelBtn>
          </Actions>
        </>
      )}
    </Form>
  )
}

export default ApiSettings

// ─── Styles ──────────────────────────────────────────────────────────────────

const Loading = styled.p`color: #777; font-size: 13px;`
const Form = styled.form`display: flex; flex-direction: column; gap: 16px;`
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px;
`
const Actions = styled.div`display: flex; gap: 8px;`
const SaveBtn = styled.button`
  height: 34px; padding: 0 16px;
  background: rgba(111,92,230,0.15); border: 1px solid rgba(111,92,230,0.35);
  border-radius: 6px; color: #a89aff; font-size: 13px; font-weight: 500; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover { background: rgba(111,92,230,0.25); border-color: rgba(111,92,230,0.5); }
`
const CancelBtn = styled.button`
  height: 34px; padding: 0 14px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px; color: #777; font-size: 13px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
`
const FeedbackBanner = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; background: rgba(51,194,176,0.08); border: 1px solid rgba(51,194,176,0.25);
  border-radius: 6px; color: #33C2B0; font-size: 13px;
`
const CloseBtn = styled.button`
  background: transparent; border: none; color: inherit; cursor: pointer; font-size: 12px; opacity: 0.7;
  &:hover { opacity: 1; }
`
